import { useEffect, useMemo, useRef, useState } from "react";
import axios from "../../api/axios";

type InfoMarkerHintProps = {
  markerKey: string;
  className?: string;
};

type MarkerItem = {
  markerKey: string;
  shortText: string;
  detailText: string;
  anchor: string;
};

let markerMapCache: Record<string, MarkerItem> | null = null;
let markerRequest: Promise<Record<string, MarkerItem>> | null = null;

async function loadMarkerMap(): Promise<Record<string, MarkerItem>> {
  if (markerMapCache) {
    return markerMapCache;
  }

  if (!markerRequest) {
    markerRequest = axios.get("/ecosystem-markers", { params: { locale: "tr" } })
      .then((response) => {
        const items = (Array.isArray(response.data?.items) ? response.data.items : []) as MarkerItem[];
        const map: Record<string, MarkerItem> = items.reduce((acc: Record<string, MarkerItem>, item: MarkerItem) => {
          if (!item?.markerKey) {
            return acc;
          }
          acc[item.markerKey] = item;
          return acc;
        }, {});
        markerMapCache = map;
        return map;
      })
      .catch(() => ({}))
      .finally(() => {
        markerRequest = null;
      });
  }

  return markerRequest;
}

export default function InfoMarkerHint({ markerKey, className }: InfoMarkerHintProps) {
  const [item, setItem] = useState<MarkerItem | null>(null);
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const containerRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    let active = true;
    void loadMarkerMap().then((map) => {
      if (!active) {
        return;
      }
      setItem(map[markerKey] ?? null);
    });

    return () => {
      active = false;
    };
  }, [markerKey]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onOutside = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) {
        return;
      }
      setOpen(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    window.addEventListener("mousedown", onOutside);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("mousedown", onOutside);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const targetHref = useMemo(() => {
    if (!item?.anchor) {
      return "/bilgilendirme";
    }
    if (item.anchor.startsWith("#")) {
      return `/bilgilendirme${item.anchor}`;
    }
    return item.anchor;
  }, [item?.anchor]);

  if (!item) {
    return null;
  }

  return (
    <span ref={containerRef} className={`relative inline-flex ${className ?? ""}`}>
      <button
        type="button"
        aria-label={item.shortText || "Bilgi"}
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600 hover:border-brand-400 hover:text-brand-500"
      >
        <span className="text-[10px] font-bold">i</span>
      </button>

      {open && (
        <div className="absolute right-0 top-7 z-20 w-72 rounded-xl border border-slate-200 bg-white p-3 shadow-xl">
          <p className="text-xs leading-5 text-slate-700">{item.shortText}</p>
          <div className="mt-2 flex items-center justify-between gap-2">
            <button
              type="button"
              className="rounded-full border border-brand-300 px-3 py-1 text-[11px] font-semibold text-brand-700"
              onClick={() => {
                setOpen(false);
                setModalOpen(true);
              }}
            >
              Detayi gor
            </button>
            <button
              type="button"
              className="rounded-full border border-slate-300 px-3 py-1 text-[11px] text-slate-500"
              onClick={() => setOpen(false)}
            >
              Kapat
            </button>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-40 bg-slate-900/70 p-3">
          <div className="mx-auto flex h-full w-full max-w-xl flex-col rounded-2xl border border-slate-200 bg-white sm:h-auto sm:max-h-[80vh]">
            <div className="flex items-start justify-between border-b border-slate-200 px-4 py-3">
              <h4 className="text-sm font-semibold text-slate-900">{item.shortText}</h4>
              <button
                type="button"
                className="rounded-full border border-slate-300 px-3 py-1 text-xs text-slate-600"
                onClick={() => setModalOpen(false)}
              >
                Kapat
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3">
              <p className="text-sm leading-6 text-slate-700">{item.detailText}</p>
            </div>
            <div className="flex justify-end border-t border-slate-200 px-4 py-3">
              <a
                href={targetHref}
                target="_blank"
                rel="noreferrer"
                className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white"
                onClick={() => setModalOpen(false)}
              >
                Bilgilendirmede aç
              </a>
            </div>
          </div>
        </div>
      )}
    </span>
  );
}

